import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
    // Auto-reload on chunk mismatch after a new version deployment
    if (error?.message && /loading chunk|loading css chunk/i.test(error.message)) {
      const lastReload = sessionStorage.getItem("chunk_reload_ts");
      const now = Date.now();
      if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
        sessionStorage.setItem("chunk_reload_ts", String(now));
        window.location.reload();
      }
    }
  }

  handleRetry = () => {
    if (this.state.error?.message && /loading chunk|loading css chunk/i.test(this.state.error.message)) {
      window.location.reload();
    } else {
      this.setState({ hasError: false, error: null });
    }
  };

  render() {
    if (this.state.hasError) {
      const isChunkError = /loading chunk|loading css chunk/i.test(this.state.error?.message || "");
      return (
        <div data-testid="error-boundary" className="flex flex-col items-center justify-center min-h-[300px] p-8">
          <AlertTriangle className="w-10 h-10 text-red-400 mb-3" />
          <h2 className="text-sm font-semibold text-zinc-200 mb-1">
            {isChunkError ? "App Updated to New Version" : "Something went wrong"}
          </h2>
          <p className="text-xs text-zinc-500 mb-4 max-w-md text-center">
            {isChunkError
              ? "A new update was deployed. Please refresh the page to load the latest dashboard."
              : this.state.error?.message || "An unexpected error occurred."}
          </p>
          <button
            data-testid="error-boundary-retry"
            onClick={this.handleRetry}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 border border-zinc-700 text-zinc-300 rounded text-xs hover:bg-zinc-700 transition-colors cursor-pointer"
          >
            <RefreshCw className="w-3 h-3" />
            {isChunkError ? "Refresh Page" : "Try Again"}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
